<template lang="pug">
    .formpage.container.content(v-if="recaptchaSiteKey && formExtensionEnabled")
        transition(name="slide-fade" mode="out-in")
            template(v-if="consent !== true")
                .card
                    .card-image
                        figure.image.is-4by3
                            picture
                                source(srcset="@/assets/images/cookie-500.jpeg 1x, @/assets/images/cookie-1000.jpeg 2x" type="image/jpeg")
                                img(src="@/assets/images/cookie.jpeg" alt="cookie")
                            small Photo by <a href="https://unsplash.com/@vyshnavibisani?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText">Vyshnavi Bisani</a> on <a href="https://unsplash.com/s/photos/cookie?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText">Unsplash</a>
                    .card-content
                        .content
                            p Wir verwenden ausschließlich essentielle Cookies auf dieser Website, die die Sicherheit beim Formularversand erhöhen.
                            p Weitere Informationen über die Verwendung Ihrer Daten finden Sie in unserer&nbsp;
                                router-link(to="datenschutz") Datenschutzerklärung
                                | .
                    footer.card-footer
                        a.card-footer-item(href @click.prevent.stop="consent = true") Ok, Formular anzeigen
            template(v-else-if="initialized")
                validation-observer(v-slot="{ invalid }" ref="observer")
                    loading(:active="form.loading" :is-full-page="false" :height="40" :width="40" color="#000000")
                    form(@submit.prevent="submit" ref="form")
                        template(
                            v-for="field in form.fields"
                            v-if="! ['email-intro', 'email-content'].includes(field.type)"
                        )
                            Field(v-if="field.type === 'multi-select'" :key="field.id" :field="field" v-model="field.values")
                            Field(v-else-if="field.type === 'upload'" :key="field.id" :field="field" v-model="field.files")
                            Field(v-else :key="field.id" :field="field" v-model="field.value")
                        .columns
                            .field.column.checkbox
                                validation-provider(name="Datenschutzbestimmungen" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }")
                                    .control
                                        label
                                            input(type="checkbox" v-model="form.privacy")
                                            |  Ich stimme den Datenschutzbestimmungen zu
                                    p.help.is-danger(v-if="errors && errors.length") {{ errors[0] }}
                                    p.help.has-text-grey(v-else="required") Pflichtfeld
                            .field.column.is-narrow
                                .control
                                    button.button.is-link.is-pulled-right(type="submit") Absenden
</template>

<script>
    import Vue from 'vue'
    import { VueReCaptcha } from 'vue-recaptcha-v3'
    import Loading from 'vue-loading-overlay'

    import { cloneDeep, filter, findIndex, get } from 'lodash'
    import { mapGetters } from 'vuex'
    import uniqid from 'uniqid'

    import store from '@/store'
    import Field from './FormField'

    export default {
        name: 'Form',
        components: {
            Error,
            Field,
            Loading,
        },
        data: () => ({
            initialized: false,
            form: {
                loading: true,
                privacy: false,
                fields: []
            },
            preset: []
        }),
        props: {
            section: {
                type: Object
            }
        },
        computed: {
            formExtensionEnabled() {
                return this.cms.enabledExtensionKeys.includes('forms')
            },
            consent: {
                get() {
                    return this.initialized || ['true', '1', 'yes', true].includes(localStorage.getItem('cookie-consent', false))
                },
                set(value) {
                    if (value === true) {
                        localStorage.setItem('cookie-consent', true)
                        this.$nextTick(() => {
                            if (this.initialized !== true) this.initialize()
                        })
                    }
                }
            },
            ...mapGetters({
                recaptchaSiteKey: 'googleRecaptchaSiteKey',
                view: 'currentPage/view',
                error: 'error',
                cms: 'CMS'
            })
        },
        methods: {
            initialize() {
                // can not get from this.consent .. local storage is  not reactive
                if (this.recaptchaSiteKey && ['true', '1', 'yes', true].includes(localStorage.getItem('cookie-consent', false))) {
                    Vue.use(VueReCaptcha, {
                        siteKey: this.recaptchaSiteKey,
                        loaderOptions: { useRecaptchaNet: true }
                    })
                    this.initialized = true
                }
            },
            async submit() {
                const valid = await this.$refs.observer.validate()
                if (valid && this.recaptchaSiteKey) {
                    this.form.loading = true
                    try {
                        await this.$recaptchaLoaded()
                        await this.$recaptcha('contact');

                        const payload = {
                            cms: { id: parseInt(this.cms.id) },
                            section: { id: parseInt(this.section.id) },
                            fields: this.form.fields,
                        }

                        store.dispatch('sendFormToApi', payload)
                            .then(() => {
                                // reset form
                                this.form.fields = cloneDeep(this.preset)
                                this.$refs.observer.reset()
                                this.$refs.form.reset()
                            })
                            .finally(() => {
                                this.form.loading = false
                            })
                    } catch (error) {
                        console.error(error);
                        const message = {
                            id: _.uniqueId(),
                            title: 'Fehler',
                            text: 'Es gibt ein technisches Problem. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                            type: 'danger',
                            autoclose: 5000
                        };
                        store.commit('addMessage', message)
                        this.form.loading = false
                    }
                }
            }
        },
        mounted() {
            const configs = get(this.section, 'contents') || []
            const sorted = configs.sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0))
            sorted.forEach(config => {

                let type
                const settings = config.settings || []
                const setting = settings.find(el => el.key === 'type')
                if (setting) type = setting.value

                let field = {
                    id: uniqid('new:'),
                    key: config.key,
                    type: type || config.key,
                    title: config.title,
                    position: config.position,
                    settings: config.settings,
                    content: { id: config.id }
                }

                // if (['multi-select', 'upload'].includes(field.type)) field.values = []
                if (field.type === 'multi-select') field.values = []
                else if (field.type === 'upload') field.files = []
                else field.value = ''

                this.form.fields.push(field)
                this.preset.push(cloneDeep(field))
            })

            this.form.loading = false
        },
        beforeMount() {
            this.initialize()
        }
    }
</script>

<style lang="sass" scoped>
.card
    max-width: 500px
    margin: 0 auto
.card-image figure
    small
        font-size: .6em
        position: absolute
        right: 6px
        bottom: 6px
        z-index: 1
    picture img
        object-fit: cover
</style>
